import { useCallback, useMemo } from 'react';

import { URLS } from '@/data';
import { isWeb } from '@/utils/constants-platform-specific';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useSelectedCountryCode } from './use-selected-country-code';

const PAGINATION_LIMIT = 100;
const WEB_DATASOURCE_SUFFIX = '-web';

export const CASINO_MIN_SEARCH_LENGTH = 3;

export const resolveCasinoDatasource = (name: string) => (isWeb ? `${name}${WEB_DATASOURCE_SUFFIX}` : name);

const stripWebDatasourceSuffix = (name: string) =>
    isWeb && name.endsWith(WEB_DATASOURCE_SUFFIX) ? name.slice(0, -WEB_DATASOURCE_SUFFIX.length) : name;

const toFilterValue = (value: string) => value.replace(/[,=]/g, ' ').trim();

/**
 * Groups are the EveryMatrix lobby category a game is placed in, as `<datasource>$<row>` ids (`Slots$all-slot-games`).
 * Web lobbies use `-web` datasources, so a game is kept if any of its groups belongs to the current platform (web or native).
 */
export const isCasinoGameAvailableOnPlatform = ({ groups }: CasinoGame) =>
    !groups?.items.length ||
    groups.items.some(groupId => groupId.split('$')[0].endsWith(WEB_DATASOURCE_SUFFIX) === isWeb);

export const buildPlatformGroupsFilter = (categoryNames: string[]) =>
    Array.from(new Set(['Lobby', ...categoryNames]))
        .map(name => `groups=${resolveCasinoDatasource(toFilterValue(name))}`)
        .join(',');

export const buildCasinoSearchFilter = (categoryNames: string[], name: string) => {
    const searchTerm = toFilterValue(name);
    return searchTerm ? `${buildPlatformGroupsFilter(categoryNames)},name=${searchTerm}` : undefined;
};

const normalizeLobbyGroupsResponse = (response: LobbyGamesResponse): LobbyGamesResponse => ({
    ...response,
    items: response.items?.map(category => ({ ...category, name: stripWebDatasourceSuffix(category.name) })),
});

export type GameListResponse = {
    count: number;
    total: number;
    items: Array<CasinoGame>;
    pages?: {
        first?: string;
        next?: string;
        last?: string;
    };
};

export type LobbyCasinoGamesCategory = {
    id: string;
    name: string;
    flags: Array<string>;
    inSchedules: boolean;
    isTopCategory: boolean;
    games: {
        count: number;
        total: number;
        items: Array<{
            href: string;
            id: string;
        }>;
        pages?: {
            first?: string;
            next?: string;
            last?: string;
        };
    };
};

export type LobbyGamesResponse = {
    count: number;
    total: number;
    items: Array<LobbyCasinoGamesCategory>;
    pages?: {
        first?: string;
        next?: string;
        last?: string;
    };
};

export type CasinoGame = {
    href: string;
    id: string;
    name: string;
    launchUrl: string;
    backgroundImageUrl: string;
    popularity: number;
    isNew: boolean;
    exclusive?: {
        effective: boolean;
    };
    width: number;
    height: number;
    hasFunMode: boolean;
    hasAnonymousMode: boolean;
    thumbnail: string;
    logo?: string;
    type: string;
    theoreticalPayOut?: number;
    volatility?: string;
    categories: {
        items: Array<{
            id: string;
            name: string;
        }>;
    };
    groups?: {
        count: number;
        items: Array<string>;
    };
    vendor?: {
        name: string;
    };
    maxBetRestriction: {
        maxBet?: {
            EUR: number;
        };
        defaultMaxBet: {
            EUR: number;
        };
    };
};

const apiGet = async <T>(url: string): Promise<T> => {
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    return response.json();
};

type UseCasinoGamesQueryParams = {
    name?: string;
    lobbyName?: string;
};

/* Get the list of casino games and filter them based on the lobby categories or name of the game */
export const useCasinoGamesQuery = (params: UseCasinoGamesQueryParams) => {
    const selectedCountryCode = useSelectedCountryCode();
    const datasource = params.lobbyName ? resolveCasinoDatasource(params.lobbyName) : undefined;
    const lobbyCategoryNames = useLobbyCategoryNames();
    const searchFilter = params.name ? buildCasinoSearchFilter(lobbyCategoryNames ?? [], params.name) : undefined;

    const buildInitialPageParam = () => {
        const queryParams = new URLSearchParams();
        if (searchFilter) {
            queryParams.append('filter', searchFilter);
        }

        if (datasource) {
            queryParams.append('datasource', datasource);
        }

        queryParams.append('Pagination', `limit=${PAGINATION_LIMIT}`);
        queryParams.append('SelectedCountryCode', selectedCountryCode);

        return `${URLS.CASINO_GAMES_API_URL}/v2/casino-new/games?${queryParams.toString()}`;
    };

    return useInfiniteQuery({
        queryKey: ['casino-games', datasource, searchFilter],
        queryFn: ({ pageParam: url }) => apiGet<GameListResponse>(url),
        initialPageParam: buildInitialPageParam(),
        getNextPageParam: lastPage => lastPage.pages?.next ?? undefined,
        enabled: Boolean(searchFilter ? lobbyCategoryNames : datasource),
    });
};

export const getLobbyGroupsNextPageParam = (lastPage: LobbyGamesResponse): string | undefined => {
    if (lastPage.pages?.next) {
        return lastPage.pages.next;
    }

    const categoryWithMoreGames = lastPage.items?.find(
        category => category.games.items.length > 0 && Boolean(category.games.pages?.next)
    );

    return categoryWithMoreGames?.games.pages?.next;
};

/* Get the list of categories belonging to a lobby */
export const useCasinoLobbyGroupsQuery = (lobbyName: string) => {
    const selectedCountryCode = useSelectedCountryCode();
    const datasource = resolveCasinoDatasource(lobbyName);

    return useInfiniteQuery({
        queryKey: ['casino-lobby-groups', datasource],
        queryFn: async ({ pageParam: url }) => normalizeLobbyGroupsResponse(await apiGet<LobbyGamesResponse>(url)),
        initialPageParam: `${URLS.CASINO_GAMES_API_URL}/v2/casino-new/groups/${datasource}?Pagination=limit=${PAGINATION_LIMIT}&SelectedCountryCode=${selectedCountryCode}`,
        getNextPageParam: getLobbyGroupsNextPageParam,
    });
};

const useLobbyCategoryNames = () => {
    const { data } = useCasinoLobbyGroupsQuery('Lobby');
    return data?.pages.flatMap(page => page.items?.map(category => category.name) ?? []);
};

/* Getting games group based on lobby categories and populate games from all casino games list  */
export const useFilteredLobbyGames = (lobbyName: string = 'Lobby') => {
    const casinoGamesQuery = useCasinoGamesQuery({ lobbyName });
    const casinoLobbyGroupsQuery = useCasinoLobbyGroupsQuery(lobbyName);
    const hasNextPage = Boolean(casinoGamesQuery.hasNextPage || casinoLobbyGroupsQuery.hasNextPage);

    const filteredGames = useMemo(() => {
        const allCasinoGames = casinoGamesQuery.data?.pages.flatMap(page => page.items ?? []) ?? [];
        // Merge paginated lobby categories
        const allLobbyGroups = casinoLobbyGroupsQuery.data?.pages ?? [];
        const categoriesMap = new Map<string, LobbyCasinoGamesCategory>();

        allLobbyGroups.forEach(page => {
            (page.items ?? []).forEach((category: LobbyCasinoGamesCategory) => {
                const existing = categoriesMap.get(category.id);
                if (existing) {
                    const existingGameIds = new Set(existing.games.items.map(g => g.id));
                    category.games.items.forEach((newGame: { href: string; id: string }) => {
                        if (!existingGameIds.has(newGame.id)) {
                            existing.games.items.push(newGame);
                            existingGameIds.add(newGame.id);
                        }
                    });
                } else {
                    categoriesMap.set(category.id, {
                        ...category,
                        games: { ...category.games, items: [...category.games.items] },
                    });
                }
            });
        });

        const allLobbyGamesCategories = Array.from(categoriesMap.values());

        if (allCasinoGames.length === 0) {
            return [];
        }

        const gamesMap = new Map(allCasinoGames.map(game => [game?.id, game]));
        return allLobbyGamesCategories.map((category: LobbyCasinoGamesCategory) => ({
            id: category.id,
            name: category.name,
            flags: category.flags,
            items: category.games.items
                .map(lobbyGame => gamesMap.get(lobbyGame.id))
                .filter((game): game is CasinoGame => game !== undefined),
        }));
    }, [casinoGamesQuery.data?.pages, casinoLobbyGroupsQuery.data?.pages]);

    const loadMoreGames = useCallback(async (): Promise<void> => {
        if (!hasNextPage || casinoGamesQuery.isFetchingNextPage || casinoLobbyGroupsQuery.isFetchingNextPage) {
            return;
        }

        const requests = [];

        if (casinoLobbyGroupsQuery.hasNextPage) {
            requests.push(casinoLobbyGroupsQuery.fetchNextPage());
        }

        if (casinoGamesQuery.hasNextPage) {
            requests.push(casinoGamesQuery.fetchNextPage());
        }

        if (requests.length) {
            await Promise.all(requests);
        }
    }, [hasNextPage, casinoGamesQuery, casinoLobbyGroupsQuery]);

    return {
        data: filteredGames,
        isLoading: casinoGamesQuery.isLoading || casinoLobbyGroupsQuery.isLoading,
        isError: casinoGamesQuery.isError || casinoLobbyGroupsQuery.isError,
        error: casinoGamesQuery.error || casinoLobbyGroupsQuery.error,
        isSuccess: casinoGamesQuery.isSuccess && casinoLobbyGroupsQuery.isSuccess,
        isFetchingNextPage: casinoGamesQuery.isFetchingNextPage || casinoLobbyGroupsQuery.isFetchingNextPage,
        loadMoreGames,
        hasNextPage,
    };
};
